import React, { useMemo, useState, useEffect, useRef } from "react";
import { TopBanner } from "./TopBanner";
import { toAbsoluteUrl } from "sr/helpers";
import { FormattedMessage } from "react-intl";
import AutoLocation from "sr/helpers/ui-components/AutoLocation";
import DropDown from "sr/helpers/ui-components/DropDown.tsx";
import { LevelBar } from "sr/helpers/ui-components/LevelBar";
import { MainWrapper } from "../../component/MainWrapper";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ReactDatePicker } from "sr/helpers/ui-components/ReactDatePicker";
import { useFormik } from "formik";
import { AdvanceDetailsSchema } from "sr/utils/schemas/company/jobPost/AdvanceDetailsSchema";
import { addAdvanceDetails } from "sr/utils/api/cleaner/company/jobPost/addAdvanceDetails";
import { getJobs } from "sr/utils/api/cleaner/jobs/getJobs";
import { useValidationErrorHandle } from "sr/helpers/functions/validationErrorHandle";
import { scrollToError } from "sr/helpers/functions/scrollToError";
import ContentWithSpinner from "sr/layout/common/ContentWithSpiner";
import { LoaderFullScreen } from "sr/layout/common/LoaderFullScreen";
import { toast } from "react-toastify";
import moment from "moment";
export const AdvanceDetails = () => {
  const [specificAddress, setspecificAddress] = useState("");
  const [applicationDeadline, setApplicationDeadline] = useState(new Date());
  const [hireCount, setHireCount] = useState();
  const [timeline, setTimeline] = useState(new Date());
  const [autoLocation, setAutoLocation] = useState({});
  const [startDate, setStartDate] = useState(new Date());
  const [reportingRegions, setReportingRegions] = useState<string[]>([]);
  const [loader, setLoader] = useState("");
  const validationErrorHandle = useValidationErrorHandle();
  const HandleAutoLocation = (data: any) => {
    setAutoLocation(data);
  };
  const params = useParams();
  const navigate = useNavigate();
  const { job_id } = params;
  let primaryRegionId = "";
  if (autoLocation) {
    const rawLoc: any = autoLocation;
    if (typeof rawLoc === "string") primaryRegionId = rawLoc;
    else if (typeof rawLoc.regionId === "string") primaryRegionId = rawLoc.regionId;
    else if (typeof rawLoc.regionId?._id === "string") primaryRegionId = rawLoc.regionId._id;
    else if (typeof rawLoc.regionId?.id === "string") primaryRegionId = rawLoc.regionId.id;
    else if (typeof rawLoc.region_id === "string") primaryRegionId = rawLoc.region_id;
    else if (typeof rawLoc.region?.id === "string") primaryRegionId = rawLoc.region.id;
    else if (typeof rawLoc.region?._id === "string") primaryRegionId = rawLoc.region._id;
    else if (typeof rawLoc.region === "string") primaryRegionId = rawLoc.region;
  }

  const additionalRegionIds = useMemo(() => {
    return (reportingRegions || []).filter(
      (id) => id && id !== primaryRegionId
    );
  }, [primaryRegionId, reportingRegions]);

  const initialValues = {
    job_id: job_id,
    reporting_address: autoLocation,
    reporting_regions: additionalRegionIds,
    hire_count: hireCount,
    recruitment_timeline: timeline || new Date(),
    application_deadline: applicationDeadline || new Date(),
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: AdvanceDetailsSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoader("submit");
      try {
        const result = await addAdvanceDetails(values);
        if (result?.success) {
          navigate(`/job-post/review/${result?.data?.job_id}`);
        }
        setLoader("");
      } catch (e) {
        toast.error("Please try again");
        setLoader("");
      }
    },
  });
  const { errors, submitForm, setFieldValue } = formik;
  const showErrors = formik.submitCount > 0;

  const fetchData = async (jobId: any) => {
    setLoader("datafetch");
    const queryParams = new URLSearchParams();
    queryParams.append("id", jobId?.toString());
    try {
      const result = await getJobs(queryParams);
      if (result?.success === true) {
        const dateObject = result?.data?.start_date;
        setStartDate(dateObject ? new Date(dateObject) : dateObject);

        const adv = result?.data?.job_advanced_id;
        if (adv?.reporting_address) {
          setAutoLocation(adv.reporting_address);
        }
        if (adv?.hire_count) setHireCount(adv.hire_count);
        if (adv?.recruitment_timeline)
          setTimeline(new Date(adv.recruitment_timeline));
        if (adv?.application_deadline)
          setApplicationDeadline(new Date(adv.application_deadline));
        const regions = Array.isArray(adv?.reporting_regions)
          ? adv.reporting_regions.map((r) => String(r)).filter(Boolean)
          : [];
        if (regions.length) {
          const advPrimary = adv?.reporting_address?.regionId
            ? String(adv.reporting_address.regionId)
            : "";
          setReportingRegions(
            advPrimary
              ? regions.filter((id) => id && id !== advPrimary)
              : regions
          );
        }
        setLoader("");
      }
    } catch (error) {
      setLoader("");
    }
  };

  const prevPrimaryRegionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!primaryRegionId) return;
    
    if (prevPrimaryRegionRef.current !== null && prevPrimaryRegionRef.current !== primaryRegionId) {
      // Region changed by user, reset additional regions completely
      setReportingRegions([]);
    } else {
      // Normal initialization filter
      setReportingRegions((prev) =>
        Array.isArray(prev)
          ? prev.filter((id) => String(id) !== primaryRegionId)
          : []
      );
    }
    prevPrimaryRegionRef.current = primaryRegionId;
  }, [primaryRegionId]);

  useEffect(() => {
    setFieldValue("reporting_regions", additionalRegionIds);
  }, [additionalRegionIds, setFieldValue]);



  useEffect(() => {
    if (job_id) {
      fetchData(job_id);
    }
  }, [job_id]);
  useEffect(() => {
    if (!showErrors) return;
    scrollToError(errors);
  }, [errors, showErrors]);






  return (
    <MainWrapper>
      <LevelBar progress={2} total={2} />
      <ContentWithSpinner loader={loader === "datafetch"} height="h-screen">
        {loader === "submit" && <LoaderFullScreen />}
        <div className="bg-white">
          <div className="pt-10 container mx-auto max-w-3xl">
            <TopBanner
              img={toAbsoluteUrl("/media/img/jobpost.png")}
              text={
                <FormattedMessage
                  id="RECRUITER.POSTJOB.ADVANCEDETAILS.TITLE"
                  defaultMessage="Advanced Details"
                />
              }
            ></TopBanner>
            <div className="pt-10">
              <AutoLocation
                HandleAutoLocation={HandleAutoLocation}
                errors={showErrors ? errors?.reporting_address : undefined}
                showMultiRegion={true}
                regions={reportingRegions}
                setRegions={setReportingRegions}
              />
              <DropDown
                error={showErrors ? errors.hire_count : undefined}
                onChange={(e) => setHireCount(e.target.value)}
                value={hireCount}
                name="selectjob"
                label={
                  <FormattedMessage
                    id="RECRUITER.POSTJOB.ADDJOBDETAILS.INPUT.NUMBEROFPEOPLE.TITLE"
                    defaultMessage="Number of people you wish to hire for this job?"
                  />
                }
                className="h-11 mt-2 border border-zinc-800 rounded-lg"
              >
                <option selected>
                  <FormattedMessage
                    id="RECRUITER.POSTJOB.ADDJOBDETAILS.INPUT.NUMBEROFPEOPLE.OPTIONS"
                    defaultMessage="Select an option"
                  />
                </option>
                {Array.from({ length: 10 }, (_, index) => (
                  <option key={index + 1} value={index + 1}>
                    {index + 1}
                  </option>
                ))}
              </DropDown>

              <ReactDatePicker
                error={showErrors ? errors.recruitment_timeline : undefined}
                InputDivClass="mt-2"
                onChange={(date) => {
                  setTimeline(date);
                  if (applicationDeadline && applicationDeadline > date) {
                    setApplicationDeadline(date);
                  }
                }}
                selected={timeline}
                minDate={startDate ? new Date(startDate) : null}
                inputClass="bg-white border border-zinc-800 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-blue-500 focus:border-[#0078ae] focus:shadow-[0_3px_0px_0px_rgb(37,87,167)] block w-full p-2 uppercase"
                label={
                  <FormattedMessage
                    id="RECRUITER.POSTJOB.ADDJOBDETAILS.INPUT.RECRUITMENTTIMELINE.TITLE"
                    defaultMessage="Position needs filled by"
                  />
                }
              ></ReactDatePicker>

              <ReactDatePicker
                InputDivClass="mt-2"
                onChange={(date) => {
                  if (timeline) {
                    const pickDate = moment(date).startOf("day");
                    const hireDate = moment(timeline).startOf("day");

                    if (pickDate.isAfter(hireDate)) {
                      toast.error(
                        "Application deadline cannot be after the hiring timeline."
                      );
                      return;
                    }
                  }

                  setApplicationDeadline(date);
                }}
                error={showErrors ? errors.application_deadline : undefined}
                selected={applicationDeadline}
                maxDate={timeline ? timeline : null}
                minDate={startDate ? new Date(startDate) : null}
                inputClass="bg-white border border-zinc-800 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-blue-500 focus:border-[#0078ae] focus:shadow-[0_3px_0px_0px_rgb(37,87,167)] block w-full p-2 uppercase"
                label={
                  <FormattedMessage
                    id="RECRUITER.POSTJOB.SETPREFERENCE.INPUT.APPLICATION.DEADLINE"
                    defaultMessage="Accepting applications until"
                  />
                }
              ></ReactDatePicker>
            </div>
            <div className="w-full mt-4 text-right flex justify-between items-center pb-10">
              <Link
                to={`/job-post/add-basic-details/${job_id}`}
                className="rounded-lg font-bold bg-white border  border-neutral-200 flex items-center justify-center p-2.5
                                text-[#0078ae] w-28 cursor-pointer hover:bg-neutral-200 hover:border-[#0078ae] space-x-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 19.5L8.25 12l7.5-7.5"
                  />
                </svg>
                <FormattedMessage
                  id="RECRUITER.POSTJOB.REVIEW.BACK.BUTTON"
                  defaultMessage="Back"
                />{" "}
              </Link>
              <div className="flex space-x-2">
                {/* <button className='rounded-lg font-bold bg-white p-2.5
                                text-[#0078ae] w-28 cursor-pointer hover:bg-neutral-200 hover:border-[#0078ae] space-x-2'>
         <FormattedMessage id="RECRUITER.POSTJOB.REVIEW.PREVIEW.BUTTON" defaultMessage="Preview" /> </button> */}
                <button
                  onClick={submitForm}
                  type="submit"
                  className="rounded-lg font-bold bg-[#ff39b3] font-bold  p-2.5  text-white  w-32 cursor-pointer hover:bg-navy"
                >
                  <FormattedMessage
                    id="RECRUITER.POSTJOB.REVIEW.PREVIEW.CONFIRM.BUTTON"
                    defaultMessage="Confirm"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </ContentWithSpinner>
    </MainWrapper>
  );
};
